/**
 * Talking to the dashboard's own API.
 *
 * Requests are same-origin: in development Vite proxies `/monitor` to the FastAPI
 * process, and in the built deployment FastAPI serves this bundle. Nothing here
 * knows a host, and no credential ever reaches the browser - the API keys stay in
 * the backend's environment, which is the whole reason the backend exists rather
 * than the page calling the health endpoints itself.
 */

import type {
  ApiKeyCheck,
  AuthStatus,
  DashboardConfig,
  HistorySummary,
  ManualTargetSpec,
  ManualTargetUpdate,
  NewCategoryRequest,
  ProbeRequest,
  ProbeResponse,
  Snapshot,
  TargetInfo,
  TargetMode,
  TargetSettings,
} from './types'

const BASE = import.meta.env.VITE_API_BASE ?? ''

/**
 * Mounted ahead of every endpoint, matching `API_PREFIX` in the backend's
 * `app/config.py` - the two must agree or every call is a 404. Declared once here and
 * joined on in `request`, so the paths below stay readable and there is a single place
 * to change if the mount point ever moves.
 */
export const API_PREFIX = '/monitor/api/v1'

/** An API call that came back with a status we cannot use. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** `path` is relative to {@link API_PREFIX}, e.g. `/snapshot`. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${API_PREFIX}${path}`, {
    ...init,
    // Send the session cookie. Needed cross-origin in the two-process dev setup, and
    // harmless same-origin - without it the gated endpoints would all read as 401.
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (!response.ok) {
    // FastAPI puts the reason in `detail`; fall back to the status text when the
    // body is something else entirely (a proxy's error page, say).
    let detail = `${response.status} ${response.statusText}`
    try {
      const body = (await response.json()) as { detail?: unknown }
      if (typeof body.detail === 'string') detail = body.detail
    } catch {
      /* Not JSON - the status line is the best description we have. */
    }
    throw new ApiError(response.status, detail)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  /** Whether auth is required, and whether this browser's session is valid. */
  authStatus: () => request<AuthStatus>('/auth/status'),

  login: (email: string, password: string) =>
    request<AuthStatus>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<AuthStatus>('/auth/logout', { method: 'POST' }),

  config: () => request<DashboardConfig>('/config'),

  /**
   * The cached picture. `refresh` forces a probe first, which costs a full round
   * trip to every host - the poll loop already keeps the cache within one interval,
   * so this is for the refresh button, not for the polling UI.
   */
  snapshot: (refresh = false) =>
    request<Snapshot>(`/snapshot${refresh ? '?refresh=true' : ''}`),

  probe: (body: ProbeRequest) =>
    request<ProbeResponse>('/probe', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  manualTargets: () => request<TargetInfo[]>('/targets/manual'),

  /** A manual monitor's editable config (host/path/scheme/label + key-lifecycle shape,
   *  never a stored key value) - for prefilling the card's edit form. */
  manualSpec: (targetId: string) =>
    request<ManualTargetSpec>(
      `/targets/manual/${encodeURIComponent(targetId)}/spec`,
    ),

  /** Edit a manual monitor. Only the fields sent change; the rest are kept. */
  updateManualTarget: (targetId: string, body: ManualTargetUpdate) =>
    request<TargetInfo>(`/targets/manual/${encodeURIComponent(targetId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /**
   * Stop watching a manually added host and forget its samples. The backend refuses
   * this for configured targets (409) - those live in `sites.json`.
   */
  deleteTarget: (targetId: string) =>
    request<void>(`/targets/${encodeURIComponent(targetId)}`, { method: 'DELETE' }),

  history: (targetId: string) =>
    request<HistorySummary>(`/history/${encodeURIComponent(targetId)}`),

  clearHistory: (targetId?: string) =>
    request<void>(
      `/history${targetId ? `?target_id=${encodeURIComponent(targetId)}` : ''}`,
      { method: 'DELETE' },
    ),

  /** Switch a target between generic (no key) and detailed (verify-or-issue) mode. */
  setMode: (targetId: string, mode: TargetMode) =>
    request<TargetSettings>(`/targets/${encodeURIComponent(targetId)}/mode`, {
      method: 'PUT',
      body: JSON.stringify({ mode }),
    }),

  /**
   * Check the key a target already holds against its verify endpoint.
   *
   * Read-only: it never issues a replacement and never discards a rejected key, so it
   * answers "is our key still good?" without rotating anything as a side effect.
   */
  checkApiKey: (targetId: string) =>
    request<ApiKeyCheck>(`/targets/${encodeURIComponent(targetId)}/check-key`, {
      method: 'POST',
    }),

  /** Give a target its own poll interval, or `null` to fall back to the global one. */
  setInterval: (targetId: string, seconds: number | null) =>
    request<TargetSettings>(`/targets/${encodeURIComponent(targetId)}/interval`, {
      method: 'PUT',
      body: JSON.stringify({ seconds }),
    }),

  /** The global default poll interval every target without its own inherits. */
  setGlobalInterval: (seconds: number) =>
    request<DashboardConfig>('/settings', {
      method: 'PUT',
      body: JSON.stringify({ poll_interval_seconds: seconds }),
    }),

  /** Add a new category (site) with its key lifecycle. */
  addCategory: (body: NewCategoryRequest) =>
    request<DashboardConfig>('/categories', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
}
